import React from "react";
import { Drawer } from "@mantine/core";
import moment from "moment";
import Icon from "./icons";
import css from "./Scheduler.module.css";
import { decrypt } from "../../../utils";

const idOf = (o) => (typeof o === "object" ? o?._id || o?.id : o);
const fmtT = (v) => (v ? moment(v).format("h:mm A") : null);
const dec = (v) => (typeof v === "string" && v.startsWith("U2FsdGVk") ? decrypt(v) : v);
// collapse consecutive duplicate words (some stored names repeat the last word)
const cleanName = (s) => (s || "").split(/\s+/).filter((w, i, a) => w.toLowerCase() !== (a[i - 1] || "").toLowerCase()).join(" ");
const STATUS = {
  draft: { tone: "grey", label: "Not submitted", cls: "sched" },
  approved: { tone: "green", label: "Approved", cls: "live" },
  pending: { tone: "amber", label: "Pending", cls: "sched" },
  rejected: { tone: "red", label: "Rejected", cls: "exc" },
};
// Draft until submitted; otherwise the approval status (grandfather old records).
const lifecycleStatus = (r) => (r?.submitted === false ? "draft" : (r?.approvedStatus || "pending"));
// Anything not yet approved can be submitted / resubmitted for approval.
const isSubmittable = (r) => r?.approvedStatus !== "approved";

const Field = ({ label, icon, value, full }) => (
  <div className={`${css.dfield} ${full ? css.full : ""}`} style={full ? { gridColumn: "1 / -1" } : {}}>
    <div className={css.lbl}>{label}</div>
    <div className={css.val}>{icon ? <Icon name={icon} size={15} /> : null}<span>{value}</span></div>
  </div>
);

// Side panel for a clock-in/out (attendance) record: clock times, client/service,
// facility and EVV (GPS) data.
export default function TimeEntryDetailDrawer({ opened, record, onClose, onSubmit, submitting, facilities = [], clients = [] }) {
  if (!record) return null;
  const r = record;
  const st = STATUS[lifecycleStatus(r)] || STATUS.pending;
  const dateStr = r.date?.date ? moment(r.date.date).format("dddd, MMM D") : r.startTime ? moment(r.startTime).format("dddd, MMM D") : "";
  const inT = fmtT(r.startTime);
  const outT = fmtT(r.endTime);
  const hours = r.total_work_time ? (Number(r.total_work_time) / 3600).toFixed(2) : null;
  const isClient = !!r.client;
  // Prefer the clean form1 name from the staff's shift clients (plaintext); fall
  // back to the record's encrypted client.name.
  const clientObj = clients.find((c) => idOf(c) === idOf(r.client));
  const clientName = (clientObj?.form1 && (clientObj.form1.firstName || clientObj.form1.lastName)
    ? cleanName(`${clientObj.form1.firstName || ""} ${clientObj.form1.lastName || ""}`.trim())
    : cleanName(dec(r.client?.name))) || "—";
  // The record has no facility field; resolve it from the client's facilityId.
  const cf = r.client?.facilityId;
  const facId = idOf(r.facility) || (Array.isArray(cf) ? cf[0] : cf);
  const facLookup = facilities.find((f) => idOf(f) === idOf(facId));
  const facName = r.facility?.name || facLookup?.name || "—";
  const facAddr = [r.facility?.address || facLookup?.address, r.facility?.city || facLookup?.city, r.facility?.state || facLookup?.state].filter(Boolean).join(", ");
  const gIn = r.evvClockIn?.location;
  const gOut = r.evvClockOut?.location;
  const evvOk = !!(gIn && gIn.lat != null);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={480}
      padding={0}
      withCloseButton={false}
      title={null}
      classNames={{ content: "sdDrawerContent" }}
      styles={{ body: { height: "100%", padding: 0 } }}
      zIndex={1000}
    >
      <div className={css.dwWrap}>
        <div className={css.dwHead}>
          <div>
            <h3 className={css.dwTitle}>Time Entry</h3>
            <div className={css.dwSub}>{[dateStr, inT && outT ? `${inT} – ${outT}` : inT].filter(Boolean).join(" · ")}</div>
          </div>
          <button type="button" className={css.dwClose} onClick={onClose} aria-label="Close"><Icon name="x" size={16} /></button>
        </div>

        <div className={css.dwBody}>
          <div className={`${css.dBanner} ${css[st.cls]}`}>
            <span className={css.bAv}><Icon name="clock" size={18} /></span>
            <div style={{ flex: 1 }}>
              <div className={css.bNm}>{hours ? `${hours}h logged` : "In progress"}</div>
              <div className={css.bSub}>{isClient ? "Client visit" : "Facility shift"}</div>
            </div>
            <span className={`${css.sBadge} ${css[st.tone]}`}>{st.label}</span>
          </div>

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Clock</h5>
            <div className={css.dgrid}>
              <Field label="Date" icon="calendar" value={dateStr || "—"} />
              <Field label="Hours logged" icon="timer" value={hours ? `${hours}h` : "—"} />
              <Field label="Clock in" icon="clock" value={inT || "—"} />
              <Field label="Clock out" icon="clock" value={outT || <span style={{ color: "#b45309" }}>Open</span>} />
            </div>
          </section>

          {Number(r.total_break_time) > 0 ? (
            <section className={css.dwSection}>
              <h5 className={css.dwSectionH}>Breaks</h5>
              <div className={css.dgrid}>
                {(r.breaks || []).filter((b) => b.breakStart || b.start).map((b, i) => (
                  <Field key={i} full label={`Break ${i + 1}`} icon="timer" value={`${fmtT(b.breakStart || b.start) || "?"} – ${fmtT(b.breakEnd || b.end) || "?"}`} />
                ))}
                <Field full label="Total break time" icon="timer" value={`${Math.round(Number(r.total_break_time) / 60)} min`} />
              </div>
            </section>
          ) : null}

          {isClient && (
            <section className={css.dwSection}>
              <h5 className={css.dwSectionH}>Client</h5>
              <div className={css.dgrid}>
                <Field full label="Name" icon="user" value={clientName || "—"} />
                {r.service ? <Field full label="Service" icon="heart" value={r.service} /> : null}
              </div>
            </section>
          )}

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>Facility</h5>
            <div className={css.dgrid}>
              <Field full label="Name" icon="hospital" value={facName || "—"} />
              {facAddr ? <Field full label="Address" icon="pin" value={facAddr} /> : null}
            </div>
          </section>

          <section className={css.dwSection}>
            <h5 className={css.dwSectionH}>EVV &amp; Time Tracking</h5>
            {evvOk ? (
              <>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                  <span className={`${css.badge2} ${css.green}`}><Icon name="check" size={11} /> GPS verified</span>
                </div>
                <div className={css.dgrid}>
                  {gIn ? <Field full label="Clock-in location" icon="pin" value={`Lat ${gIn.lat}, Lng ${gIn.lng}${r.evvClockIn?.serviceAddress ? ` · ${r.evvClockIn.serviceAddress}` : ""}`} /> : null}
                  {gOut ? <Field full label="Clock-out location" icon="pin" value={`Lat ${gOut.lat}, Lng ${gOut.lng}${r.evvClockOut?.serviceAddress ? ` · ${r.evvClockOut.serviceAddress}` : ""}`} /> : null}
                </div>
              </>
            ) : (
              <div className={css.excClear}><Icon name="alert" size={15} /> No GPS / EVV data recorded for this entry.</div>
            )}
          </section>
        </div>

        <div className={css.dwFoot}>
          <button type="button" className={css.btnG} onClick={onClose}>Cancel</button>
          <span style={{ flex: 1 }} />
          {onSubmit && isSubmittable(r) ? (
            <button type="button" className={css.btnP} onClick={onSubmit} disabled={submitting}>
              <Icon name="send" size={16} /> {r.approvedStatus === "rejected" ? "Resubmit" : "Submit for approval"}
            </button>
          ) : null}
        </div>
      </div>
    </Drawer>
  );
}
